/**
 * Human Design — the calculated layer.
 *
 * `humandesign.ts` works out WHAT is switched on: gates, channels, centres,
 * type, authority, variables. This file answers the questions a reader asks
 * once they are looking at that: which circuits am I wired into, what does
 * "Split Definition" actually mean in MY chart, and which gate would close the
 * gap. All of it is graph work over the 36 channels — nothing here invents a
 * reading.
 *
 * It is the same treatment the I Ching tab got, for the same reason: a chart
 * that only names things leaves the reader to look everything up elsewhere.
 *
 * The gate→circuit table is NOT typed from memory. It was extracted from the
 * project's own I Ching knowledge base (`D:\Humen Design\docs\iching`), whose
 * per-gate Human Design block names the circuit, and then cross-checked: for
 * all 36 channels the two end gates report the same circuit, 0 disagreements.
 * That check is worth re-running if this table is ever edited.
 */

import { CHANNELS, GATE_CENTER, CENTER_GATES } from './humandesign';

// ------------------------------------------------------------- circuitry

export type CircuitId =
  | 'iKnow' | 'iCentre' | 'integration'
  | 'cLogic' | 'cAbstract'
  | 'tDefense' | 'tEgo';

export const GATE_CIRCUIT: Record<number, CircuitId> = {
  1: 'iKnow', 2: 'iKnow', 3: 'iKnow', 4: 'cLogic', 5: 'cLogic', 6: 'tDefense', 7: 'cLogic', 8: 'iKnow',
  9: 'cLogic', 10: 'integration', 11: 'cAbstract', 12: 'iKnow', 13: 'cAbstract', 14: 'iKnow', 15: 'cLogic', 16: 'cLogic',
  17: 'cLogic', 18: 'cLogic', 19: 'tEgo', 20: 'integration', 21: 'tEgo', 22: 'iKnow', 23: 'iKnow', 24: 'iKnow',
  25: 'iCentre', 26: 'tEgo', 27: 'tDefense', 28: 'iKnow', 29: 'cAbstract', 30: 'cAbstract', 31: 'cLogic', 32: 'tEgo',
  33: 'cAbstract', 34: 'integration', 35: 'cAbstract', 36: 'cAbstract', 37: 'tEgo', 38: 'iKnow', 39: 'iKnow', 40: 'tEgo',
  41: 'cAbstract', 42: 'cAbstract', 43: 'iKnow', 44: 'tEgo', 45: 'tEgo', 46: 'cAbstract', 47: 'cAbstract', 48: 'cLogic',
  49: 'tEgo', 50: 'tDefense', 51: 'iCentre', 52: 'cLogic', 53: 'cAbstract', 54: 'tEgo', 55: 'iKnow', 56: 'cAbstract',
  57: 'integration', 58: 'cLogic', 59: 'tDefense', 60: 'iKnow', 61: 'iKnow', 62: 'cLogic', 63: 'cLogic', 64: 'cAbstract',
};

export interface CircuitMeta {
  id: CircuitId; family: 'individual' | 'collective' | 'tribal' | 'integration';
  th: string; en: string; colour: string;
  /** What the circuit is FOR — standard HD, kept short and marked as reading. */
  aboutTh: string; aboutEn: string;
  /** How it tends to show up in a portfolio. */
  marketTh: string; marketEn: string;
}

export const CIRCUITS: CircuitMeta[] = [
  {
    id: 'iKnow', family: 'individual', th: 'ปัจเจก · การรู้เอง', en: 'Individual / Knowing', colour: '#a78bfa',
    aboutTh: 'พลังกลายพันธุ์ — รู้เองเป็นจังหวะ ไม่ใช่ตามตรรกะหรือตามหมู่คณะ',
    aboutEn: 'Mutation — knowing that arrives in pulses, neither logical nor collective.',
    marketTh: 'ไอเดียมาเป็นช่วง ๆ และมักมาก่อนตลาด — แต่บังคับให้มาตามตารางไม่ได้ ระบบที่ดีคือบันทึกไว้แล้วรอจังหวะ',
    marketEn: 'Ideas arrive in bursts, often early — they cannot be scheduled. Log them and wait for the entry.',
  },
  {
    id: 'iCentre', family: 'individual', th: 'ปัจเจก · แกนกลาง', en: 'Individual / Centering', colour: '#c4b5fd',
    aboutTh: 'ความกล้าที่มาจากภายใน — แรงกระตุ้นให้ก้าวข้ามความกลัวของตัวเอง',
    aboutEn: 'Courage from within — the push to move through your own fear.',
    marketTh: 'กล้าเข้าไม้ที่คนอื่นไม่กล้า แต่ต้องแยกให้ออกระหว่าง "กล้า" กับ "ดื้อ"',
    marketEn: 'Willing to take the trade others will not — the work is telling courage from stubbornness.',
  },
  {
    id: 'integration', family: 'integration', th: 'บูรณาการ · เอาตัวรอด', en: 'Integration', colour: '#f0abfc',
    aboutTh: 'สี่ประตู 10·20·34·57 ที่พันกันเอง — การเอาตัวรอดของปัจเจกในปัจจุบันขณะ',
    aboutEn: 'Gates 10·20·34·57 wired only to each other — self-survival, in the present moment.',
    marketTh: 'ตัดสินใจเร็วมากในสถานการณ์จริง แต่แผนที่วางล่วงหน้ามักไม่ตรงกับสิ่งที่ทำจริง',
    marketEn: 'Very fast in a live situation; plans made in advance rarely match what you actually do.',
  },
  {
    id: 'cLogic', family: 'collective', th: 'ส่วนรวม · ตรรกะ', en: 'Collective / Understanding', colour: '#38bdf8',
    aboutTh: 'รูปแบบที่ทำซ้ำได้ — ต้องพิสูจน์ก่อนจึงเชื่อ และแบ่งปันให้ทุกคนใช้ได้',
    aboutEn: 'Repeatable patterns — proof before belief, then shared with everyone.',
    marketTh: 'ระบบ แบ็กเทสต์ กฎที่เขียนออกมาได้ — วงจรนี้คือคนที่ควรมีเช็กลิสต์',
    marketEn: 'Systems, backtests, written rules — this circuit is why checklists work for you.',
  },
  {
    id: 'cAbstract', family: 'collective', th: 'ส่วนรวม · ประสบการณ์', en: 'Collective / Sensing', colour: '#22d3ee',
    aboutTh: 'บทเรียนจากประสบการณ์ — เข้าใจย้อนหลัง แล้วเล่าต่อเป็นเรื่องราว',
    aboutEn: 'Lessons from experience — understood in hindsight, then told as a story.',
    marketTh: 'เก่งเรื่องอ่านวัฏจักรและบริบท แต่แม่นเมื่อมองย้อน — อย่าใช้ความรู้สึก "เคยเห็นแบบนี้" แทนสัญญาณ',
    marketEn: 'Strong on cycles and context, but sharpest in hindsight — do not let "I have seen this before" replace a signal.',
  },
  {
    id: 'tDefense', family: 'tribal', th: 'เผ่า · ปกป้อง', en: 'Tribal / Defense', colour: '#fb923c',
    aboutTh: 'สัญชาตญาณปกป้องคนของตัวเอง — ระดับพันธุกรรม ไม่ผ่านความคิด',
    aboutEn: 'The instinct to protect your own — genetic, not thought through.',
    marketTh: 'ตัดสินใจเรื่องเงินโดยคิดถึงครอบครัวก่อนเสมอ — เป็นเบรกที่ดี ถ้ารู้ตัวว่ากำลังใช้มันอยู่',
    marketEn: 'Money decisions run through "who depends on me" first — a good brake, if you know it is engaged.',
  },
  {
    id: 'tEgo', family: 'tribal', th: 'เผ่า · ข้อตกลงและทรัพยากร', en: 'Tribal / Ego', colour: '#f0c75a',
    aboutTh: 'การต่อรอง ข้อตกลง และการแบ่งทรัพยากร — คุณค่าวัดกันที่สิ่งที่ให้ได้จริง',
    aboutEn: 'Bargains, agreements and resources — worth measured by what you can actually deliver.',
    marketTh: 'เก่งเรื่องดีล ราคาต่อรอง และการจัดสรรเงิน — ระวังการเอาผลตอบแทนมาวัดคุณค่าตัวเอง',
    marketEn: 'Strong on deals, pricing and allocation — watch for measuring self-worth in returns.',
  },
];

export const CIRCUIT_BY_ID = new Map(CIRCUITS.map(c => [c.id, c]));

/** A channel sits in one circuit; both its gates agree (checked for all 36). */
export const channelCircuit = (a: number, b: number): CircuitId => GATE_CIRCUIT[a] ?? GATE_CIRCUIT[b];

// ----------------------------------------------------------- the centres

export type CentreRole = 'pressure' | 'awareness' | 'motor' | 'identity' | 'expression';

export interface CentreMeta {
  id: string; th: string; en: string; role: CentreRole;
  roleTh: string; roleEn: string;
}

/**
 * The nine centres with the job each one does. Role is what makes an open
 * centre readable: an open MOTOR is borrowed energy, an open AWARENESS centre
 * is borrowed certainty, and open PRESSURE is someone else's urgency.
 */
export const CENTRES: CentreMeta[] = [
  { id: 'head', th: 'ศีรษะ', en: 'Head', role: 'pressure', roleTh: 'ศูนย์แรงกดดัน', roleEn: 'pressure centre' },
  { id: 'ajna', th: 'อาชญะ', en: 'Ajna', role: 'awareness', roleTh: 'ศูนย์การรับรู้', roleEn: 'awareness centre' },
  { id: 'throat', th: 'ลำคอ', en: 'Throat', role: 'expression', roleTh: 'ศูนย์การแสดงออก', roleEn: 'expression centre' },
  { id: 'g', th: 'ตัวตน (G)', en: 'G Centre', role: 'identity', roleTh: 'ศูนย์ตัวตนและทิศทาง', roleEn: 'identity and direction' },
  { id: 'heart', th: 'หัวใจ', en: 'Heart / Ego', role: 'motor', roleTh: 'มอเตอร์', roleEn: 'motor' },
  { id: 'sacral', th: 'ศักดิ์สิทธิ์', en: 'Sacral', role: 'motor', roleTh: 'มอเตอร์ (พลังชีวิต)', roleEn: 'motor (life force)' },
  // `splenic`, not `spleen` — that is the key CENTER_GATES uses, and reading
  // the wrong one silently yields an empty gate list rather than an error.
  { id: 'splenic', th: 'ม้าม', en: 'Spleen', role: 'awareness', roleTh: 'ศูนย์การรับรู้ (สัญชาตญาณ)', roleEn: 'awareness centre (instinct)' },
  { id: 'solar', th: 'อารมณ์', en: 'Solar Plexus', role: 'motor', roleTh: 'มอเตอร์ + การรับรู้ทางอารมณ์', roleEn: 'motor + emotional awareness' },
  { id: 'root', th: 'ราก', en: 'Root', role: 'pressure', roleTh: 'ศูนย์แรงกดดัน + มอเตอร์', roleEn: 'pressure centre + motor' },
];

// ------------------------------------------------------ definition, read

export interface DefinitionRead {
  /** Centres grouped into the islands the channels actually connect. */
  groups: string[][];
  /** Gates that would join two different islands into one. */
  bridges: { gate: number; withGate: number; joins: [string, string] }[];
  label: string;
}

/**
 * Why "Split Definition" is not a label but a map.
 *
 * The centres a chart defines are not automatically one piece: the channels
 * form islands. Which islands, and which single gate would join them, is the
 * useful part — that gate is the thing a reader keeps meeting in other people,
 * and the reason certain company makes them feel whole.
 *
 * Islands are the connected components of the centre graph. A bridge is any
 * channel whose two centres sit in different islands and where the reader
 * already holds one end.
 */
export function readDefinition(definedChannels: [number, number][], activeGates: number[]): DefinitionRead {
  const parent: Record<string, string> = {};
  const find = (x: string): string => (parent[x] === x ? x : (parent[x] = find(parent[x])));
  const union = (a: string, b: string) => { parent[find(a)] = find(b); };

  const centres = new Set<string>();
  for (const [a, b] of definedChannels) { centres.add(GATE_CENTER[a]); centres.add(GATE_CENTER[b]); }
  for (const c of centres) parent[c] = c;
  for (const [a, b] of definedChannels) union(GATE_CENTER[a], GATE_CENTER[b]);

  const byRoot = new Map<string, string[]>();
  for (const c of centres) {
    const r = find(c);
    byRoot.set(r, [...(byRoot.get(r) || []), c]);
  }
  const groups = [...byRoot.values()].sort((x, y) => y.length - x.length);

  const active = new Set(activeGates);
  const bridges: DefinitionRead['bridges'] = [];
  if (groups.length > 1) {
    const groupOf = new Map<string, number>();
    groups.forEach((g, i) => g.forEach(c => groupOf.set(c, i)));
    for (const [a, b] of CHANNELS) {
      const ga = groupOf.get(GATE_CENTER[a]);
      const gb = groupOf.get(GATE_CENTER[b]);
      if (ga == null || gb == null || ga === gb) continue;
      // One end already lit means the other end is the gate that would bridge.
      if (active.has(a) && !active.has(b)) bridges.push({ gate: b, withGate: a, joins: [GATE_CENTER[a], GATE_CENTER[b]] });
      else if (active.has(b) && !active.has(a)) bridges.push({ gate: a, withGate: b, joins: [GATE_CENTER[b], GATE_CENTER[a]] });
    }
  }

  const label = groups.length === 0 ? 'none' : groups.length === 1 ? 'single' : groups.length === 2 ? 'split' : groups.length === 3 ? 'triple' : 'quadruple';
  return { groups, bridges, label };
}

// ------------------------------------------------------------- summaries

export interface CircuitTally { id: CircuitId; meta: CircuitMeta; channels: [number, number][]; }

/** Which circuits the reader's completed channels actually belong to. */
export function circuitTally(definedChannels: [number, number][]): CircuitTally[] {
  const by = new Map<CircuitId, [number, number][]>();
  for (const ch of definedChannels) {
    const id = channelCircuit(ch[0], ch[1]);
    by.set(id, [...(by.get(id) || []), ch]);
  }
  return CIRCUITS
    .map(meta => ({ id: meta.id, meta, channels: by.get(meta.id) || [] }))
    .filter(x => x.channels.length > 0)
    .sort((a, b) => b.channels.length - a.channels.length);
}

/** Gates a centre holds, split by whether the reader has them switched on. */
export function centreGates(centre: string, activeGates: number[]) {
  const active = new Set(activeGates);
  const gates = CENTER_GATES[centre] || [];
  return { gates, on: gates.filter(g => active.has(g)), off: gates.filter(g => !active.has(g)) };
}

/**
 * How the reader's 26 activations spread over the six line positions.
 *
 * The I Ching tab draws the same tally from the same numbers, which is the
 * point: a Human Design profile IS two of these positions, so seeing the whole
 * distribution shows how much of the chart the profile actually speaks for.
 */
export function lineSpread(activations: { line: number }[]): number[] {
  const out = [0, 0, 0, 0, 0, 0];
  for (const a of activations) if (a.line >= 1 && a.line <= 6) out[a.line - 1]++;
  return out;
}

/**
 * The Incarnation Cross as its four gates rather than one opaque string.
 * Personality Sun/Earth then Design Sun/Earth — the order the cross is named in.
 */
export function crossGates(personality: any[], design: any[]) {
  const pick = (list: any[], k: string) => list.find(a => a.k === k) || null;
  const ps = pick(personality, 'sun'), pe = pick(personality, 'earth');
  const ds = pick(design, 'sun'), de = pick(design, 'earth');
  return [
    { role: 'Personality Sun', roleTh: 'ดวงอาทิตย์ (จิตสำนึก)', a: ps },
    { role: 'Personality Earth', roleTh: 'โลก (จิตสำนึก)', a: pe },
    { role: 'Design Sun', roleTh: 'ดวงอาทิตย์ (ร่างกาย)', a: ds },
    { role: 'Design Earth', roleTh: 'โลก (ร่างกาย)', a: de },
  ].filter(x => x.a);
}
